import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { FileText, Plus, Calendar, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAlert } from '../../context/AlertContext';

const StudiesList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('site_id');
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    site_id: siteId || '',
    study_name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    production_basis: 'MT',
    status: 'Draft',
    currency: 'INR'
  });

  useEffect(() => {
    fetchData();
  }, [siteId]);

  const fetchData = async () => {
    try {
      const [studiesRes, sitesRes] = await Promise.all([
        axios.get(`${API}/ers/studies${siteId ? `?site_id=${siteId}` : ''}`),
        axios.get(`${API}/ers/sites`)
      ]);
      setStudies(studiesRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      await axios.post(`${API}/ers/studies`, payload);
      setShowAddModal(false);
      setFormData({
        site_id: siteId || '',
        study_name: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        production_basis: 'MT',
        status: 'Draft',
        currency: 'INR'
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create study:', error);
      showError('Failed to create study: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Ongoing': 'bg-blue-100 text-blue-700',
      'Review': 'bg-yellow-100 text-yellow-700',
      'Final': 'bg-green-100 text-green-700'
    };
    return colors[status] || colors['Draft'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ERS Studies</h1>
              <p className="text-gray-600 mt-2">Manage energy and resource studies</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/energy-resources')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
              >
                ← Back to ERS Dashboard
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                New Study
              </button>
            </div>
          </div>
        </div>

        {/* Studies Grid */}
        {studies.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No studies yet</h3>
            <p className="text-gray-600 mb-6">Create your first ERS study to begin</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5 inline-block mr-2" />
              New Study
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {studies.map((study) => (
              <div
                key={study.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-purple-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(study.status)}`}>
                    {study.status}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{study.study_name}</h3>
                {study.site_name && (
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <MapPin className="w-4 h-4 mr-1" />
                    {study.site_name}
                  </div>
                )}
                {study.company_name && (
                  <p className="text-sm text-gray-600 mb-3">{study.company_name}</p>
                )}
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <Calendar className="w-4 h-4 mr-1" />
                  {study.baseline_start && study.baseline_end 
                    ? `Baseline: ${study.baseline_start} to ${study.baseline_end}`
                    : `Start: ${new Date(study.start_date).toLocaleDateString()}`
                  }
                </div>
                <div className="pt-3 border-t border-gray-200 mb-3">
                  <div className="text-xs text-gray-600">
                    Production Basis: <span className="font-semibold">{study.production_basis}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/ers/study/${study.id}`)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all text-sm shadow-md"
                >
                  Open Study →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add Study Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New ERS Study</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => setFormData({...formData, site_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Select a site</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name} - {site.company_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Study Name *</label>
                <input
                  type="text"
                  value={formData.study_name}
                  onChange={(e) => setFormData({...formData, study_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Energy Audit 2025"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Production Basis *</label>
                <select
                  value={formData.production_basis}
                  onChange={(e) => setFormData({...formData, production_basis: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="MT">MT (Metric Tons)</option>
                  <option value="UNIT">UNIT (Production Units)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This determines how intensities will be calculated
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="Draft">Draft</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Review">Under Review</option>
                  <option value="Final">Final</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="INR">₹ (INR - Indian Rupees)</option>
                  <option value="SGD">S$ (SGD - Singapore Dollar)</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  Create Study
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StudiesList;
